import cx from 'classnames';
import { isEqual, sortBy } from 'lodash-es';
import Papa from 'papaparse';
import type { ChangeEvent, FormEvent } from 'react';
import { useState } from 'react';
import { isValidPhoneNumber } from 'react-phone-number-input/input';
import { Button, ButtonVariants } from '@components/button/Button';
import { errorToast, successToast } from '@components/toasts/Toasts';
import { reportAppError } from '@shared/reportAppError';
import { isValidEmail } from '@shared/utils/emailValidator';
import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import typography from '~styles/typography.scss';
import { PageContent } from '../layout/PageContent';
import { PageHeader } from '../layout/PageHeader';
import { type ImportedGuest, importGuests } from './apiHelpers';
import styles from './GuestImport.scss';
import { sanitizePhoneNumber } from './utils';

const HEADERS: Record<string, string> = {
  firstName: 'First Name *',
  lastName: 'Last Name *',
  phone: 'Phone (optional)',
  email: 'Email (optional)',
  notes: 'Notes (optional)',
  tags: 'Tags (optional)',
};
const REQUIRED_HEADERS = Object.keys(HEADERS);

const validators: Record<string, (value: string) => boolean> = {
  firstName: (firstName: string) => !!firstName,
  lastName: (lastName: string) => !!lastName,
  phone: (phone: string) => {
    if (!phone) return true;
    return isValidPhoneNumber(phone);
  },
  email: (email: string) => {
    if (!email) return true;
    return isValidEmail(email);
  },
  notes: (notes: string) => notes.length <= 600,
};

export const GuestImport = () => {
  const { id: restaurantId } = useRestaurant();
  const [parsedData, setParsedData] = useState<string[][]>([]);
  const [hasInvalidData, setHasInvalidData] = useState(false);
  const [hasInvalidHeaders, setHasInvalidHeaders] = useState(false);

  const handleOnSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const [_headers, ...rows] = parsedData;

    const toImportedGuest = (row: string[]) => ({
      firstName: row[0],
      lastName: row[1],
      phone: row[2] || null,
      email: row[3] || null,
      notes: row[4] || null,
      tags: row[5] ? row[5].split(',') : null,
    });

    const importedGuestsJson = rows.map<ImportedGuest>(toImportedGuest);

    const handleImportedGuests = async () => {
      try {
        await importGuests(restaurantId, importedGuestsJson);

        successToast({ message: 'Guests imported successfully' });
      } catch (e) {
        errorToast({ message: 'Failed to import guests' });
        reportAppError(e);
      }
    };
    void handleImportedGuests();
  };

  const sanitizeData = (data: string[][]) => {
    const [_header, ...rows] = data;

    const sanitizedRows = rows.map((row) => {
      const sanitizedRow = row;
      const phone = sanitizedRow[2];
      if (phone.length > 0) {
        sanitizedRow[2] = sanitizePhoneNumber(phone);
      }
      return sanitizedRow;
    });

    return [_header, ...sanitizedRows];
  };

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    setHasInvalidData(false);
    setHasInvalidHeaders(false);
    setParsedData([]);
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    Papa.parse(file, {
      skipEmptyLines: true,
      complete: ({ data }: { data: string[][] }) => {
        const [headers, ...rows] = data;

        const hasValidHeaders = isEqual(
          sortBy(headers),
          sortBy(REQUIRED_HEADERS),
        );

        if (hasValidHeaders && rows.length > 0) {
          const sanitizedData = sanitizeData(data);
          setParsedData(sanitizedData);
        } else {
          setHasInvalidHeaders(true);
        }
      },
    });
  };

  const [headers, ...rows] = parsedData;

  const hasError = (field: string, header: string) => {
    if (validators[header]) {
      const isValid = validators[header](field);
      if (!isValid && !hasInvalidData) setHasInvalidData(true);
      return !validators[header](field);
    }
    return false;
  };

  return (
    <>
      <PageHeader title="Guest Import" />
      <PageContent className={styles.container}>
        <form onSubmit={handleOnSubmit}>
          <Button
            isDisabled={!rows.length || hasInvalidData}
            label="Import Guests"
            type="submit"
            variant={ButtonVariants.Primary}
          />
          <label htmlFor="guest-importer">Upload CSV</label>
          <input
            type="file"
            accept=".csv"
            id="guest-importer"
            onChange={handleOnChange}
          />

          <p
            className={cx({
              [styles.error]: hasInvalidHeaders || hasInvalidData,
            })}
          >
            To be importable, a CSV must contain at least 1 row and only contain
            the following headers: firstName, lastName, phone, email, notes, and
            tags.
          </p>
        </form>
        {!!parsedData.length && (
          <table>
            <thead>
              <tr>
                <th className={typography.c1}>1</th>
                {headers.map((header) => (
                  <th className={typography.c1} key={header}>
                    {HEADERS[header]}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {rows.map((row, rowIndex) => (
                // eslint-disable-next-line react/no-array-index-key
                <tr key={rowIndex}>
                  <td className={typography.c3}>{rowIndex + 2}</td>

                  {row.map((field, columnIndex) => (
                    <td
                      className={cx(typography.c3, {
                        [styles.error]: hasError(field, headers[columnIndex]),
                      })}
                      // eslint-disable-next-line react/no-array-index-key
                      key={columnIndex}
                    >
                      {field}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </PageContent>
    </>
  );
};
